import { SearchOutlined } from "@ant-design/icons";
import { Card, Skeleton, Tag } from "antd";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useLocation } from "react-router-dom";
import HTTP from "../../services/httpService";
import CustomHeader from "../common/Header";
import CustomTable from "../common/Table";

const baseColumns = [
    {
        title: "Project",
        dataIndex: "project_name",
        key: "project_id",
        sorter: () => {},
        render: (name) => (
            <span
                style={{
                    color: "var(--color-primary)",
                    fontWeight: 500,
                    fontSize: 13,
                }}
            >
                {name}
            </span>
        ),
    },
    {
        title: "Ticket",
        dataIndex: "ticket_id",
        key: "ticket_id",
        sorter: () => {},
        render: (id) =>
            id ? (
                <span
                    style={{
                        fontFamily: "monospace",
                        fontSize: 12,
                        background: "var(--color-bg-elevated)",
                        border: "1px solid var(--color-border)",
                        borderRadius: 5,
                        padding: "2px 8px",
                        color: "var(--color-primary-hover)",
                    }}
                >
                    {id}
                </span>
            ) : (
                <span style={{ color: "var(--color-text-muted)" }}>—</span>
            ),
    },
    {
        title: "Title",
        dataIndex: "title",
        key: "title",
        sorter: () => {},
        width: 180,
        render: (title) => (
            <span
                style={{
                    color: "var(--color-text)",
                    fontSize: 13,
                    fontWeight: 500,
                }}
                title={title}
            >
                {title?.length > 30 ? `${title.substring(0, 30)}…` : title}
            </span>
        ),
    },
    {
        title: "Commit Message",
        dataIndex: "message",
        key: "message",
        sorter: () => {},
        width: 200,
        render: (msg) => (
            <span
                style={{ color: "var(--color-text-secondary)", fontSize: 12 }}
                title={msg}
            >
                {msg?.length > 40 ? `${msg.substring(0, 40)}…` : msg}
            </span>
        ),
    },
    {
        title: "Author",
        dataIndex: "author_name",
        key: "author_email",
        sorter: () => {},
        render: (name) => (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div
                    style={{
                        width: 28,
                        height: 28,
                        borderRadius: 8,
                        background: "var(--color-bg-elevated)",
                        border: "1px solid var(--color-border)",
                        color: "var(--color-primary)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        fontSize: 11,
                        flexShrink: 0,
                    }}
                >
                    {name ? name[0].toUpperCase() : "?"}
                </div>
                <span style={{ color: "var(--color-text)", fontSize: 13 }}>
                    {name}
                </span>
            </div>
        ),
    },
    {
        title: "Branch",
        dataIndex: "branch_name",
        key: "branch_name",
        sorter: () => {},
        render: (branch) =>
            branch ? (
                <Tag
                    style={{
                        background: "var(--color-accent-light)",
                        border: "1px solid var(--color-accent)",
                        color: "var(--color-accent)",
                        borderRadius: 5,
                        fontSize: 11,
                        fontFamily: "monospace",
                        padding: "1px 8px",
                    }}
                >
                    {branch}
                </Tag>
            ) : (
                <span style={{ color: "var(--color-text-muted)" }}>—</span>
            ),
    },
    {
        title: "Committer",
        dataIndex: "committer_name",
        key: "committer_email",
        sorter: () => {},
        render: (name) => (
            <span style={{ color: "var(--color-text-secondary)", fontSize: 13 }}>
                {name || "—"}
            </span>
        ),
    },
    {
        title: "Commit ID",
        dataIndex: "commit_id",
        key: "commit_id",
        sorter: () => {},
        render: (id) =>
            id ? (
                <span
                    style={{
                        fontFamily: "monospace",
                        fontSize: 11,
                        background: "var(--color-bg-card)",
                        border: "1px solid var(--color-border)",
                        borderRadius: 4,
                        padding: "2px 7px",
                        color: "var(--color-text-muted)",
                    }}
                >
                    {String(id).substring(0, 8)}…
                </span>
            ) : (
                "—"
            ),
    },
    {
        title: "Committed At",
        dataIndex: "commit_created_at",
        key: "commit_created_at",
        sorter: () => {},
        render: (date) => (
            <span style={{ color: "var(--color-text-muted)", fontSize: 12 }}>
                {date || "—"}
            </span>
        ),
    },
    {
        title: "Lines Changed",
        render: (_, record) => (
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <span
                    style={{
                        color: "var(--color-success)",
                        fontWeight: 600,
                        fontSize: 12,
                        background: "var(--color-success-light)",
                        borderRadius: 5,
                        padding: "2px 8px",
                        border: "1px solid var(--color-success)",
                    }}
                >
                    +{record.added_lines}
                </span>
                <span
                    style={{
                        color: "var(--color-error)",
                        fontWeight: 600,
                        fontSize: 12,
                        background: "var(--color-error-light)",
                        borderRadius: 5,
                        padding: "2px 8px",
                        border: "1px solid var(--color-error)",
                    }}
                >
                    -{record.deleted_lines}
                </span>
            </div>
        ),
    },
];

function GitDashboardTable() {
    const staff_id = localStorage.getItem("x-dashboard-staffid");
    const { state } = useLocation();
    const { range_year, project_id, user_id, range_month } = state;
    const [totalRecords, setTotalRecords] = useState(0);
    const [tableData, setTableData] = useState([]);
    const [columns, setColumns] = useState(baseColumns);
    const [formData, setFormData] = useState({
        staff_id,
        range_month,
        range_year,
        project_id,
        user_id,
        page: 0,
        limit: 10,
    });

    const {
        status: gitCommitStatus,
        data: gitCommitRes,
        refetch: getGitCommits,
    } = useQuery(
        "gitCommitTable",
        async () =>
            await HTTP("/analytics/getGitCommitsMonthlyTableData", {
                ...formData,
            }),
        { enabled: false },
    );
    const {
        status: commiterStatus,
        data: commiterRes,
        refetch: getCommiters,
    } = useQuery(
        "gitCommiters",
        async () =>
            await HTTP("/analytics/getGitCommitterEmails", { staff_id }),
        { enabled: false },
    );
    const {
        status: gitProjectStatus,
        data: gitProjectRes,
        refetch: getGitProjects,
    } = useQuery(
        "gitProjects",
        async () => await HTTP("/analytics/getGitProjects", { staff_id }),
        { enabled: false },
    );
    const {
        status: authorStatus,
        data: authorRes,
        refetch: getAuthors,
    } = useQuery(
        "gitAuthors",
        async () => await HTTP("/analytics/getGitAuthorEmails", { staff_id }),
        { enabled: false },
    );

    useEffect(() => {
        getAuthors();
        getCommiters();
        getGitProjects();
        getGitCommits();
    }, []);
    useEffect(() => {
        getGitCommits();
    }, [formData]);

    const filterIcon = (filtered) => (
        <SearchOutlined
            style={{
                color: filtered
                    ? "var(--color-primary)"
                    : "var(--color-text-muted)",
            }}
        />
    );

    useEffect(() => {
        if (commiterStatus === "success") {
            setColumns((prev) =>
                prev.map((col) => {
                    if (col.dataIndex !== "committer_name") return col;
                    return {
                        ...col,
                        width: "10%",
                        filterIcon,
                        filterSearch: true,
                        filters: commiterRes.data.map((c) => ({
                            text: c.committer_name,
                            value: c.committer_email,
                        })),
                    };
                }),
            );
        }
    }, [commiterStatus, commiterRes]);

    useEffect(() => {
        if (authorStatus === "success") {
            setColumns((prev) =>
                prev.map((col) => {
                    if (col.dataIndex !== "author_name") return col;
                    return {
                        ...col,
                        width: "10%",
                        filterIcon,
                        filterSearch: true,
                        filters: authorRes.data.map((a) => ({
                            text: a.author_name,
                            value: a.author_email,
                        })),
                    };
                }),
            );
        }
    }, [authorStatus, authorRes]);

    useEffect(() => {
        if (gitProjectStatus === "success") {
            setColumns((prev) =>
                prev.map((col) => {
                    if (col.dataIndex !== "project_name") return col;
                    return {
                        ...col,
                        width: "10%",
                        filterIcon,
                        filterSearch: true,
                        filters: gitProjectRes.data.map((p) => ({
                            text: p.project_name,
                            value: p.project_id,
                        })),
                    };
                }),
            );
        }
    }, [gitProjectStatus, gitProjectRes]);

    useEffect(() => {
        if (gitCommitStatus === "success") {
            setTableData(gitCommitRes.data);
            setTotalRecords(gitCommitRes.total);
        }
    }, [gitCommitStatus, gitCommitRes]);

    function handleTableChange(pagination, filters, sorter) {
        const { current, pageSize } = pagination;
        const { order, field } = sorter;
        const others = { page: current - 1, limit: pageSize };
        if (order !== undefined) {
            others.sorting = order === "ascend" ? 1 : 2;
            others.sort_by = field;
        } else {
            delete formData.sorting;
            delete formData.sort_by;
        }
        setFormData({ ...formData, ...others, filters });
    }

    const isLoading =
        gitCommitStatus === "loading" || gitCommitStatus === "idle";

    return (
        <Card className="dashboard-container">
            <CustomHeader />
            {isLoading ? (
                <Skeleton
                    active
                    title={false}
                    paragraph={{ rows: 20, width: "100%" }}
                />
            ) : (
                tableData.length > 0 && (
                    <CustomTable
                        columns={columns}
                        tableData={tableData}
                        noOfrecords={totalRecords}
                        handleTableChange={handleTableChange}
                        rowKey="id"
                        className="dashboard-table"
                        isScroll
                    />
                )
            )}
        </Card>
    );
}

export { GitDashboardTable };
